<template>
  <van-popup
    :show="modelValue"
    position="bottom"
    round
    safe-area-inset-bottom
    class="country-code-popup"
    @update:show="handleShowChange"
  >
    <section class="country-code-picker">
      <van-search
        v-model="keyword"
        shape="round"
        clearable
        class="country-code-picker__search"
        :placeholder="searchPlaceholder"
      >
        <template #left-icon>
          <app-image
            :src="searchIcon"
            alt=""
            class="country-code-picker__search-icon"
          />
        </template>
      </van-search>

      <section ref="listRef" class="country-code-picker__list">
        <template v-for="section in groupedItems" :key="section.key">
          <div
            :ref="(el) => setSectionRef(section.key, el)"
            class="country-code-picker__section"
          >
            <div class="country-code-picker__section-title">
              {{ section.title }}
            </div>

            <button
              v-for="item in section.items"
              :key="`${section.key}-${item.code}-${item.dial_code}`"
              type="button"
              class="country-code-picker__item"
              :class="{ active: selectedCode === item.dial_code }"
              @click="selectItem(item)"
            >
              <span class="country-code-picker__flag">
                <app-image
                  v-if="countryFlagUrl(item.code) && !failedFlags[item.code]"
                  :src="countryFlagUrl(item.code)"
                  :alt="displayName(item)"
                  @error="markFlagFailed(item.code)"
                />
                <span v-else>{{ countryCodeEmoji(item.code) }}</span>
              </span>
              <div class="country-code-picker__meta">
                <div data-strong>{{ item.dial_code }}</div>
                <span>{{ displayName(item) }}</span>
              </div>
              <AppSvgIcon
                v-if="selectedCode === item.dial_code"
                name="icon"
                size="18"
                color="var(--wp-color-theme)"
                class="country-code-picker__status"
              />
              <app-image
                v-else
                :src="uncheckedIcon"
                alt=""
                class="country-code-picker__status"
              />
            </button>
          </div>
        </template>

        <van-empty
          v-if="!groupedItems.length"
          image="search"
          :description="emptyText"
        />
      </section>

      <aside v-if="showIndexBar" class="country-code-picker__index">
        <button
          v-for="section in groupedItems"
          :key="`index-${section.key}`"
          type="button"
          class="country-code-picker__index-item"
          @click="scrollToSection(section.key)"
        >
          {{
            section.key === "common"
              ? isZhLocale(locale)
                ? "常"
                : "C"
              : section.key
          }}
        </button>
      </aside>
    </section>
  </van-popup>
</template>

<script setup>
import { computed, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import {
  countryCodeEmoji,
  countryCodeItems,
  countryFlagUrl,
  groupCountryCodeItems,
} from "@/utils/country-code";
import { getLocalizedCountryCodeName, isZhLocale } from "@/utils/display-name";
import searchIcon from "@/assets/images/search_icon.png";
import uncheckedIcon from "@/assets/images/circleNo_gray_icon.png";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  selectedCode: {
    type: String,
    default: "",
  },
  title: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["update:modelValue", "select"]);

const { t, locale } = useI18n();
const keyword = ref("");
const listRef = ref(null);
const failedFlags = reactive({});
const sectionRefs = reactive({});

const searchPlaceholder = computed(() => t("card.selectCountryCode"));
const emptyText = computed(() => t("common.noData"));

const filteredItems = computed(() => {
  const searchValue = String(keyword.value || "")
    .trim()
    .toLowerCase();
  if (!searchValue) {
    return countryCodeItems;
  }

  return countryCodeItems.filter((item) => {
    const dialCode = String(item?.dial_code || "").toLowerCase();
    const names = [displayName(item), item?.nameCn, item?.nameEn, item?.name]
      .filter(Boolean)
      .map((name) => String(name).toLowerCase());
    const code = String(item?.code || "").toLowerCase();
    return (
      dialCode.includes(searchValue) ||
      names.some((name) => name.includes(searchValue)) ||
      code.includes(searchValue)
    );
  });
});

const groupedItems = computed(() =>
  groupCountryCodeItems(filteredItems.value).map((section) => ({
    ...section,
    title:
      section.key === "common"
        ? isZhLocale(locale.value)
          ? "常用"
          : "Common"
        : section.title,
  })),
);
const showIndexBar = computed(
  () => !String(keyword.value || "").trim() && groupedItems.value.length > 1,
);

watch(
  () => props.modelValue,
  (visible) => {
    if (!visible) {
      keyword.value = "";
    }
  },
);

function handleShowChange(value) {
  emit("update:modelValue", value);
}

function close() {
  emit("update:modelValue", false);
}

function setSectionRef(key, element) {
  if (element) {
    sectionRefs[key] = element;
  }
}

function displayName(item) {
  return getLocalizedCountryCodeName(item, locale.value) || "--";
}

function selectItem(item) {
  emit("select", item);
  close();
}

function scrollToSection(key) {
  const target = sectionRefs[key];
  if (!target) {
    return;
  }
  target.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
}

function markFlagFailed(code) {
  if (code) {
    failedFlags[code] = true;
  }
}
</script>

<style scoped lang="scss">
.country-code-picker {
  position: relative;
  padding: 0 0 calc(18px + env(safe-area-inset-bottom));
  background: #ffffff;
}

.country-code-picker__search {
  margin: 16px 16px 12px;

  :deep(.van-search) {
    padding: 0;
    background: transparent;
  }

  :deep(.van-search__content) {
    background: #f4f5f6;
  }
  :deep(.van-field) {
    background: none;
  }
  :deep(.van-field__control),
  :deep(.van-field__control::placeholder) {
    font-size: 14px;
  }
}

.country-code-picker__search-icon {
  display: block;
  width: 15px;
  height: 15px;
}

.country-code-picker__list {
  max-height: 60vh;
  overflow-y: auto;
  padding: 0 28px 0 16px;
}

.country-code-picker__section-title {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 8px 0 6px;
  background: #ffffff;
  color: #86909c;
  font-size: 13px;
  line-height: 18px;
}

.country-code-picker__item {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 12px;
  padding: 12px 4px;
  border: 0;
  border-bottom: 1px solid #f2f3f5;
  background: transparent;
  color: #1f2430;
  text-align: left;
}

.country-code-picker__flag {
  display: flex;
  flex: 0 0 40px;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 30px;
  overflow: hidden;
  border-radius: 6px;
  background: #f7f8fa;
  font-size: 22px;
  line-height: 1;
  text-align: center;

  .app-image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.country-code-picker__meta {
  display: flex;
  min-width: 0;
  flex: 1;
  align-items: center;
  gap: 10px;

  [data-strong] {
    flex: 0 0 62px;
    font-size: 16px;
    font-weight: 500;
  }

  span {
    min-width: 0;
    color: #4e5969;
    font-size: 13px;
    line-height: 18px;
    word-break: break-word;
  }
}

.country-code-picker__status {
  display: block;
  width: 18px;
  height: 18px;
  margin-left: auto;
}

.country-code-picker__index {
  position: absolute;
  top: 96px;
  right: 4px;
  bottom: calc(18px + env(safe-area-inset-bottom));
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 20px;
}

.country-code-picker__index-item {
  width: 20px;
  height: 16px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #86909c;
  font-size: 11px;
  line-height: 16px;
  text-align: center;
}
</style>
